import React, { useRef, useState } from 'react';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  InputNumber,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { styled } from 'styled-components';

import { MonitorMetric, ThresholdRecommendation } from './types';
import Service from '@/lib/App';

interface MetricsSectionProps {
  metrics: MonitorMetric[];
  onChange: (metrics: MonitorMetric[]) => void;
  disabled?: boolean;
  variableName: string;
}

type ValidationState = {
  loading: boolean;
  result?: { valid: boolean; value?: number; error?: string };
};

type RecommendState = {
  loading: boolean;
  fetched?: boolean;
  recommendation?: ThresholdRecommendation;
  recommendationWarning?: string;
  noData?: boolean;
  error?: string;
};

function getRecommendSubText(
  windowsCovered?: string[],
  recommendationWarning?: string,
): string {
  if (recommendationWarning) return recommendationWarning;
  if (
    windowsCovered?.includes('recent_2h') &&
    windowsCovered?.includes('week_ago_2h')
  ) {
    return 'Based on recent data and data from 7 days ago';
  }
  if (windowsCovered?.includes('recent_2h')) {
    return 'Based on recent data only';
  }
  return 'Based on historical data';
}

const MetricsSection: React.FC<MetricsSectionProps> = ({
  metrics,
  onChange,
  disabled = false,
  variableName,
}) => {
  const [validationStates, setValidationStates] = useState<
    Record<string, ValidationState>
  >({});
  const [recommendStates, setRecommendStates] = useState<
    Record<string, RecommendState>
  >({});
  // Keep a ref so async callbacks always see the latest metrics list without
  // stale-closure issues (same pattern as monitorsRef in monitors-section).
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;

  const handleMetricChange = (
    index: number,
    field: keyof MonitorMetric,
    value: string | number | null,
  ) => {
    if (value === null) return;
    if ((field === 'min' || field === 'max') && typeof value === 'number') {
      value = Math.round(value * 100) / 100;
    }
    const updatedMetrics = [...metrics];
    const clearValidated =
      field === 'query' ? { queryValidated: undefined } : {};
    updatedMetrics[index] = {
      ...updatedMetrics[index],
      [field]: value,
      ...clearValidated,
    };
    onChange(updatedMetrics);
    const metricKey = metrics[index]?.id ?? String(index);
    if (field === 'query') {
      setValidationStates((prev) => {
        const next = { ...prev };
        delete next[metricKey];
        return next;
      });
      setRecommendStates((prev) => {
        const next = { ...prev };
        delete next[metricKey];
        return next;
      });
    }
  };

  const handleAddMetric = () => {
    onChange([
      ...metrics,
      { id: crypto.randomUUID(), displayName: '', query: '', min: 0, max: 100 },
    ]);
  };

  const handleRemoveMetric = (index: number) => {
    const metricKey = metrics[index]?.id ?? String(index);
    onChange(metrics.filter((_, i) => i !== index));
    setValidationStates((prev) => {
      const next = { ...prev };
      delete next[metricKey];
      return next;
    });
    setRecommendStates((prev) => {
      const next = { ...prev };
      delete next[metricKey];
      return next;
    });
  };

  const handleTestQuery = async (index: number) => {
    const metric = metrics[index];
    const metricKey = metric?.id ?? String(index);
    const query = metric?.query?.trim();
    if (!query) return;

    setValidationStates((prev) => ({
      ...prev,
      [metricKey]: { loading: true },
    }));

    const resp = await Service.planner!.validateCanaryQuery({
      query,
      variable_name: variableName,
    });

    const result = { valid: resp.valid, value: resp.value, error: resp.error };
    setValidationStates((prev) => ({
      ...prev,
      [metricKey]: { loading: false, result },
    }));

    // Use metricsRef to merge the result into the latest list, avoiding
    // stale-closure overwrites of edits made while the request was in-flight.
    onChange(
      metricsRef.current.map((m) =>
        m.id === metricKey ? { ...m, queryValidated: resp.valid === true } : m,
      ),
    );
  };

  const handleFetchRecommendation = async (index: number) => {
    const metric = metrics[index];
    const metricKey = metric?.id ?? String(index);
    const query = metric?.query?.trim();
    if (!query) return;

    setRecommendStates((prev) => ({
      ...prev,
      [metricKey]: { loading: true },
    }));

    const resp = await Service.planner!.validateCanaryQuery({
      query,
      variable_name: variableName,
      fetch_recommendation: true,
    });

    // A recommendation call also validates the query — persist that result.
    // Use metricsRef to merge the result into the latest list, avoiding
    // stale-closure overwrites of edits made while the request was in-flight.
    if (resp.valid !== undefined) {
      onChange(
        metricsRef.current.map((m) =>
          m.id === metricKey
            ? { ...m, queryValidated: resp.valid === true }
            : m,
        ),
      );
      setValidationStates((prev) => ({
        ...prev,
        [metricKey]: {
          loading: false,
          result: { valid: resp.valid, value: resp.value, error: resp.error },
        },
      }));
    }

    setRecommendStates((prev) => ({
      ...prev,
      [metricKey]: {
        loading: false,
        fetched: true,
        recommendation: resp.recommendation,
        recommendationWarning: resp.recommendationWarning,
        noData: resp.valid && !resp.recommendation && !resp.error,
        error: resp.valid ? undefined : resp.error,
      },
    }));
  };

  const handleApplyRecommendation = (
    index: number,
    rec: ThresholdRecommendation,
  ) => {
    const updatedMetrics = [...metrics];
    updatedMetrics[index] = {
      ...updatedMetrics[index],
      min: rec.suggestedMin,
      max: rec.suggestedMax,
    };
    onChange(updatedMetrics);
  };

  const handleDismissRecommendation = (metricKey: string) => {
    setRecommendStates((prev) => {
      const next = { ...prev };
      delete next[metricKey];
      return next;
    });
  };

  return (
    <Wrapper>
      <Header>
        <Typography.Text strong>Metric Queries</Typography.Text>
        <Typography.Text type="secondary">
          Custom Datadog queries with min/max thresholds
        </Typography.Text>
      </Header>
      <TableContainer>
        <TableInner>
          <TableHead>
            <ColName>Metric Name</ColName>
            <ColQuery>Datadog Query</ColQuery>
            <ColThreshold>Min</ColThreshold>
            <ColThreshold>Max</ColThreshold>
            <ColActions />
          </TableHead>
          {metrics.map((metric, index) => {
            const metricKey = metric.id ?? String(index);
            const vs = validationStates[metricKey];
            const rs = recommendStates[metricKey];
            const hasQuery = !!metric.query?.trim();
            return (
              <div key={metricKey}>
                <TableRow>
                  <ColName>
                    <Input
                      placeholder="Name"
                      value={metric.displayName}
                      disabled={disabled}
                      onChange={(e) =>
                        handleMetricChange(index, 'displayName', e.target.value)
                      }
                      size="small"
                    />
                  </ColName>
                  <ColQuery>
                    <Input
                      placeholder="e.g. avg:trace.http.request.duration{service:my-svc}"
                      value={metric.query}
                      disabled={disabled}
                      onChange={(e) =>
                        handleMetricChange(index, 'query', e.target.value)
                      }
                      size="small"
                    />
                  </ColQuery>
                  <ColThreshold>
                    <InputNumber
                      placeholder="Min"
                      value={metric.min}
                      step={0.01}
                      precision={2}
                      disabled={disabled}
                      onChange={(value) =>
                        handleMetricChange(index, 'min', value)
                      }
                      size="small"
                      style={{ width: '100%' }}
                    />
                  </ColThreshold>
                  <ColThreshold>
                    <InputNumber
                      placeholder="Max"
                      value={metric.max}
                      step={0.01}
                      precision={2}
                      disabled={disabled}
                      onChange={(value) =>
                        handleMetricChange(index, 'max', value)
                      }
                      size="small"
                      style={{ width: '100%' }}
                    />
                  </ColThreshold>
                  <ColActions>
                    <Tooltip title="Test query">
                      <span>
                        <Button
                          type="text"
                          size="small"
                          icon={
                            vs?.loading ? (
                              <LoadingOutlined {...({} as any)} />
                            ) : vs?.result?.valid ? (
                              <CheckCircleOutlined
                                {...({} as any)}
                                style={{ color: '#52c41a' }}
                              />
                            ) : vs?.result && !vs.result.valid ? (
                              <CloseCircleOutlined
                                {...({} as any)}
                                style={{ color: '#ff4d4f' }}
                              />
                            ) : (
                              <ThunderboltOutlined {...({} as any)} />
                            )
                          }
                          onClick={() => handleTestQuery(index)}
                          disabled={disabled || !hasQuery}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title="Get threshold recommendations">
                      <span>
                        <Button
                          type="text"
                          size="small"
                          icon={
                            rs?.loading ? (
                              <LoadingOutlined {...({} as any)} />
                            ) : (
                              <SparkleIcon>✦</SparkleIcon>
                            )
                          }
                          onClick={() => handleFetchRecommendation(index)}
                          disabled={disabled || !hasQuery}
                        />
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove metric">
                      <span>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined {...({} as any)} />}
                          onClick={() => handleRemoveMetric(index)}
                          disabled={disabled}
                        />
                      </span>
                    </Tooltip>
                  </ColActions>
                </TableRow>
                {vs?.result && (
                  <ValidationRow>
                    {vs.result.valid ? (
                      <ValidationSuccess>
                        Query valid — current value: {vs.result.value}
                      </ValidationSuccess>
                    ) : (
                      <ValidationError>{vs.result.error}</ValidationError>
                    )}
                  </ValidationRow>
                )}
                {rs && !rs.loading && rs.fetched && (
                  <RecommendRow>
                    {rs.error ? (
                      <ValidationError>{rs.error}</ValidationError>
                    ) : rs.noData ? (
                      <NoDataNote>
                        Not enough historical data to generate a recommendation.
                      </NoDataNote>
                    ) : rs.recommendation ? (
                      <RecommendContent>
                        <RecommendHeader>
                          <RecommendLabel>
                            <SparkleIcon>✦</SparkleIcon>
                            Recommended thresholds
                            {rs.recommendation.confidence === 'high' ? (
                              <ConfidenceBadge high>
                                High confidence
                              </ConfidenceBadge>
                            ) : (
                              <ConfidenceBadge>
                                Partial confidence
                              </ConfidenceBadge>
                            )}
                          </RecommendLabel>
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined {...({} as any)} />}
                            onClick={() =>
                              handleDismissRecommendation(metricKey)
                            }
                            style={{ color: '#8c8c8c' }}
                          />
                        </RecommendHeader>
                        <RecommendValues>
                          <span>
                            Min:{' '}
                            <strong>
                              {rs.recommendation.suggestedMin != null
                                ? rs.recommendation.suggestedMin.toFixed(2)
                                : '-'}
                            </strong>
                          </span>
                          <span>
                            Max:{' '}
                            <strong>
                              {rs.recommendation.suggestedMax != null
                                ? rs.recommendation.suggestedMax.toFixed(2)
                                : '-'}
                            </strong>
                          </span>
                        </RecommendValues>
                        <RecommendSubText>
                          {getRecommendSubText(
                            rs.recommendation.windowsCovered,
                            rs.recommendationWarning,
                          )}
                        </RecommendSubText>
                        {!disabled && (
                          <Button
                            size="small"
                            type="primary"
                            style={{ alignSelf: 'flex-start' }}
                            onClick={() =>
                              handleApplyRecommendation(
                                index,
                                rs.recommendation!,
                              )
                            }
                          >
                            Apply recommendations
                          </Button>
                        )}
                      </RecommendContent>
                    ) : null}
                  </RecommendRow>
                )}
              </div>
            );
          })}
        </TableInner>
      </TableContainer>
      <Button
        type="dashed"
        size="small"
        icon={<PlusOutlined {...({} as any)} />}
        onClick={handleAddMetric}
        disabled={disabled}
        style={{ alignSelf: 'flex-start' }}
      >
        Add Metric
      </Button>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const TableContainer = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  overflow-x: auto;
  overflow-y: hidden;
`;

const TableInner = styled.div`
  min-width: 560px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  gap: 8px;
`;

const TableHead = styled(Row)`
  background: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  font-weight: 600;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  padding: 8px 8px;
`;

const TableRow = styled(Row)`
  border-bottom: 1px solid #f0f0f0;
`;

const ColName = styled.div`
  width: 110px;
  flex-shrink: 0;
`;

const ColQuery = styled.div`
  flex: 1;
  min-width: 120px;
  max-width: 280px;
  overflow: hidden;
`;

const ColThreshold = styled.div`
  width: 76px;
  flex-shrink: 0;
`;

const ColActions = styled.div`
  width: 76px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ValidationRow = styled.div`
  padding: 4px 8px 8px 8px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
`;

const RecommendRow = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  font-size: 14px;
`;

const RecommendContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RecommendHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RecommendLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
`;

const RecommendValues = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const RecommendSubText = styled.div`
  color: #8c8c8c;
  font-size: 12px;
`;

const NoDataNote = styled.span`
  color: #8c8c8c;
  font-style: italic;
`;

const ConfidenceBadge = styled.span<{ high?: boolean }>`
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  background: ${(p) => (p.high ? '#f6ffed' : '#fff7e6')};
  color: ${(p) => (p.high ? '#52c41a' : '#fa8c16')};
  border: 1px solid ${(p) => (p.high ? '#b7eb8f' : '#ffd591')};
  font-weight: 500;
`;

const ValidationSuccess = styled.span`
  color: #52c41a;
`;

const ValidationError = styled.span`
  color: #ff4d4f;
`;

const SparkleIcon = styled.span`
  font-size: 13px;
  color: #722ed1;
`;

export default MetricsSection;
